import { AbsgArrayHelper } from '@absg/angular-common';
import { dateMaskOptions } from '@absg/angular-components';
import { DateTimeHelper } from '@absg/angular-platform';
import { Component, ElementRef, forwardRef, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { IDateInputComponent } from 'shared.root/models/date-input-component.interface.';

@Component({
  selector: '[nc-date-input]',
  templateUrl: './date-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DateInputComponent implements IDateInputComponent, ControlValueAccessor, Validator {
  private _maxDateString: string;
  private _minDateString: string;
  dateMask: IMask.AnyMaskedOptions;

  @Input() dateControlName = '';
  @Input() set maxDateString(value: string) {
    if (!value || value === '') {
      this._maxDateString = DateTimeHelper.formatDateTime(DateTimeHelper.addYears(new Date(), 1), DateTimeHelper.defaultDateFormat);
    } else {
      this._maxDateString = value;
    }
    this.maxDate = new Date(this._maxDateString);
  }

  get maxDateString(): string {
    return this._maxDateString;
  }

  @Input() set minDateString(value: string) {
    if (!value || value === '') {
      this._minDateString = '2000-01-01';
    } else {
      this._minDateString = value;
    }
    this.minDate = new Date(this._minDateString);
  }

  get minDateString(): string {
    return this._minDateString;
  }

  @Input() isRequired = false;
  @Input() showRequiredIndicator = true;
  @Input() hasError = false;
  @Input() isInline = false;
  @Input() invalidDateMessage = DateTimeHelper.invalidDateMessage;
  @Input() invalidDateMessageAlternate = '';
  @Input() calendarPickerHeader = '';
  @Input() showAlternateErrorMessage = false;

  isDatePickerModalOpen = new BehaviorSubject<boolean>(false);
  modalSelectedDate = new Date();
  selectedDate = '';
  defaultDateFormat: string;
  defaultDateFormatDisplay: string;
  disabled: boolean;
  tabIndex: number;
  isDateInputError: boolean;

  private minDate: Date;
  private maxDate: Date;

  constructor(private element: ElementRef) {
    this.defaultDateFormat = DateTimeHelper.defaultDateFormat;
    this.defaultDateFormatDisplay = this.defaultDateFormat.toUpperCase();
    // force defaults to be created
    this.minDateString = null;
    this.maxDateString = null;
    this.isDateInputError = false;

    this.dateMask = AbsgArrayHelper.cloneDeep(dateMaskOptions);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.dateMask as any).autofix = false;
  }

  writeValue(obj: string): void {
    if (obj) {
      this.selectedDate = obj;
      this.modalSelectedDate = new Date(obj);
    } else if (obj === '') {
      this.selectedDate = obj;
      this.modalSelectedDate = new Date();
    }
  }

  registerOnChange(fn: (_: string) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(): void { }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setTabIndex(tabIndex = 0): void {
    this.tabIndex = tabIndex;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate(c: AbstractControl): ValidationErrors | any {
    // other validators can be injected, this is just default validation
    if (c) {
      if (this.isRequired && (c.value === null || c.value === '')) {
        return { validationError: true };
      }
      if (c.value != null && c.value !== '') {
        if (c.value.length === 10) {
          if (!this.isInputValid(c.value)) {
            return { validationError: true };
          }
        } else {
          return { validationError: true };
        }
      }
    }
    return null;
  }

  onCalendarButtonClicked(): void {
    this.isDatePickerModalOpen.next(true);
  }

  onDateModalSave(selectedDate: Date): void {
    this.isDatePickerModalOpen.next(false);
    this.modalSelectedDate = selectedDate;
    this.selectedDate = DateTimeHelper.formatDateTime(selectedDate, DateTimeHelper.defaultDateFormat);
    this.triggerChangeEvent(this.selectedDate);
  }

  onDateModalCancel(): void {
    this.isDatePickerModalOpen.next(false);
  }

  onDateClear(): void {
    this.isDatePickerModalOpen.next(false);
    this.selectedDate = '';
    this.modalSelectedDate = new Date();
    this.triggerChangeEvent(this.selectedDate);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur(event: any): void {
    const newValue = event.target.value as string;
    this.setDatesForCalendarModal(newValue);
    this.triggerBlurEvent();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange(event: any): void {
    const newValue = event.target.value as string;
    if (newValue === '') { // cleared the field, reset to today's date
      this.modalSelectedDate = new Date();
      this.selectedDate = '';
    } else if (this.isInputValid(newValue)) {
      this.selectedDate = newValue;
      this.modalSelectedDate = new Date(newValue);
    } else {
      this.selectedDate = newValue;
      this.modalSelectedDate = new Date();
    }
    this.triggerChangeEvent(newValue);
  }

  setDatesForCalendarModal(dateValue: string): void {
    if (dateValue === '') { // cleared the field, reset to today's date
      this.selectedDate = '';
      this.modalSelectedDate = new Date();
      this.isDateInputError = false;
    } else if (this.isInputValid(dateValue)) {
      const date = new Date(dateValue);
      this.selectedDate = DateTimeHelper.formatDateTime(date, DateTimeHelper.defaultDateFormat);
      this.modalSelectedDate = date;
      this.isDateInputError = false;
    } else {
      this.selectedDate = dateValue;
      this.modalSelectedDate = new Date();
      this.isDateInputError = true;
    }
  }

  isInputValid(inputDateString: string): boolean {
    const inputDate = new Date(inputDateString);
    return DateTimeHelper.isDateStringValid(inputDateString) && (inputDate >= this.minDate && inputDate <= this.maxDate);
  }

  private triggerBlurEvent(): void {
    const event = new CustomEvent('blur', { bubbles: true });
    this.element.nativeElement.dispatchEvent(event);
  }

  private triggerChangeEvent(newValue: string): void {
    this.propagateChange(newValue);
    const event = new CustomEvent('change', { bubbles: true });
    this.element.nativeElement.dispatchEvent(event);
  }

  private propagateChange = (_: string) => { };
}
